<template>
  <div class="space-y-32">
    <div
      v-if="ratings.length"
      class="bg-surface-default-mid rounded-lg"
      :class="gridClasses"
    >
      <div v-for="(value, valueIndex) in ratings" :key="valueIndex">
        <RevRating
          v-if="value.count"
          class="p-12 flex-wrap"
          :score="value.averageRate || 0"
          :size="ratingSize"
          :to="value.url"
          :total-reviews="
            i18n(translations.reviewsCount, { count: value.count })
          "
        />
      </div>
    </div>
    <div v-if="tableData" class="space-y-48 lg:space-y-64">
      <div
        v-for="({ title, sections, separator }, tableGroupIndex) in tableData"
        :key="`table-group-${tableGroupIndex}`"
        :class="[
          separator === true &&
            'border-static-default-low border-t pt-48 lg:pt-64 first:border-t-0 first:pt-0',
        ]"
      >
        <h2 class="heading-2 mb-4">{{ title }}</h2>
        <div class="divide-static-default-low space-y-48 divide-y">
          <div
            v-for="({ title: sectionTitle, lines }, sectionIndex) in sections"
            :key="`${tableGroupIndex}_${sectionIndex}`"
            class="pt-48 first:pt-0"
          >
            <h3
              v-if="sectionTitle"
              class="heading-1 !font-punchline-sans lg:punchline-sans mb-12"
            >
              {{ sectionTitle }}
            </h3>
            <div class="space-y-20">
              <div
                v-for="(line, lineIndex) in lines"
                :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}`"
              >
                <div v-if="line.type === 'scores'" :class="gridClasses">
                  <div
                    v-for="(score, scoreIndex) in line.values"
                    :key="scoreIndex"
                  >
                    <ProductScore
                      v-if="score"
                      :label="sectionTitle || ''"
                      :product-name="productNames?.[scoreIndex]"
                      :value="score"
                    />
                  </div>
                </div>
                <RevInfoBlock
                  v-else-if="line.type === 'info-block'"
                  :icon="IconLightbulb"
                  :title="line.title"
                  variant="info"
                >
                  <!-- eslint-disable vue/no-v-html -->
                  <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -- Safe: HTML sourced from Back Market's own API, not user input -->
                  <div :class="$style.textBlock" v-html="line.value" />
                </RevInfoBlock>
                <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -- Safe: HTML sourced from Back Market's own API, not user input -->
                <div
                  v-else-if="line.type === 'html'"
                  :class="$style.textBlock"
                  v-html="line.value"
                />
                <div v-else-if="line.type === 'variants'">
                  <p v-if="line.title" class="body-1-bold mb-8">
                    {{ line.title }}
                  </p>
                  <div
                    class="bg-surface-default-mid rounded-lg p-12"
                    :class="gridClasses"
                  >
                    <div
                      v-for="(items, colIndex) in line.values"
                      :key="colIndex"
                      class="space-y-8"
                    >
                      <button
                        v-for="(item, itemIndex) in items"
                        :key="itemIndex"
                        :aria-disabled="!item.price"
                        class="bg-static-default-low border-static-default-low block w-full rounded-sm border py-12 px-16 text-left"
                        :class="{
                          'opacity-50': !item.price,
                        }"
                        @click="item.price && openVariant(item.url)"
                      >
                        <div
                          class="body-1 flex items-center gap-8 text-static-default-hi"
                        >
                          <RevColorSwatch
                            v-if="item.color"
                            :color="item.color"
                          />
                          {{ item.label }}
                        </div>
                        <p class="body-2 text-static-default-mid mt-4">
                          <template v-if="item.price">
                            {{ i18n(translations.startingAt) }}:
                            {{ item.price.amount }} {{ item.price.currency }}
                          </template>
                          <template v-else>
                            {{ i18n(translations.alreadySold) }}
                          </template>
                        </p>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <p v-if="line.title" class="body-1-bold mb-8">
                    {{ line.title }}
                  </p>
                  <div
                    class="bg-surface-default-mid rounded-lg"
                    :class="gridClasses"
                  >
                    <div
                      v-for="(value, valueIndex) in line.values"
                      :key="valueIndex"
                    >
                      <ul
                        v-if="line.type === 'tags'"
                        class="flex list-none flex-wrap items-start gap-x-4 gap-y-8 p-12"
                      >
                        <li
                          v-for="(label, labelIndex) in value"
                          :key="labelIndex"
                          class="flex"
                        >
                          <RevTag
                            v-if="typeof label === 'string'"
                            :label
                            variant="secondary"
                            variation="outline"
                          />
                        </li>
                      </ul>
                      <h3 v-else class="text-static-info-hi heading-2 p-12">
                        {{ value }}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { RevColorSwatch } from '@ds/components/ColorSwatch'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { IconLightbulb } from '@ds/icons/IconLightbulb'
import { useMounted } from '@vueuse/core'

import type { RatingValue, TableData } from '../../../models/productComparison'
import ProductScore from '../ProductScore/ProductScore.vue'

import translations from './TableContent.translations'

const i18n = useI18n()

function openVariant(url: string) {
  window.open(url, '_blank', 'noopener noreferrer')
}

const { tableData, ratings } = defineProps<{
  tableData: TableData[]
  ratings: RatingValue[]
  productNames?: string[]
}>()

const gridClasses = 'grid grid-cols-2 gap-x-24 lg:gap-x-40'

const isMounted = useMounted()
const breakpoint = useClientOnlyComputedBreakpoint()
const ratingSize = computed(() =>
  !isMounted.value || breakpoint.value < Breakpoint.MD ? 'small' : 'medium',
)
</script>

<style module>
.textBlock {
  b,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    display: block;
    font-weight: bold;
  }

  p:not(:first-child),
  ul:not(:first-child),
  li:not(:first-child) {
    margin-top: 20px;
  }
}
</style>
